/* ============================================================
   Dathent — funnel.js (TASK-122, issue #214, EPIC-019/EPIC-018)
   Lightweight client-side funnel-event tracker for the conversion
   diagnosis: signup -> onboarding -> first value -> paywall -> subscribe.
   ~88/144 signups drop during the onboarding-business step and the whole
   web channel converts at 0% vs. MCP/ChatGPT — this is the instrumentation
   needed to see WHERE, not just guess from code reading.

   DARK by default — gated end-to-end behind funnelAnalyticsEnabled()
   ('dathent.funnel.analytics', see lib/flags.js). While the flag is off,
   every exported function is a complete no-op: no console output, no
   network call, no localStorage write.

   Backend: POSTs to `${apiBase}/api/funnel/ingest` — a dedicated,
   unauthenticated gateway route (dathent-api-gateway-worker src/routes/
   funnel.ts, modeled on the /api/ux/ingest precedent), NOT
   `/api/analytics/events`: that path proxies to analytics-worker behind
   requireActiveBilling and 402s unpaid accounts, which is exactly the
   population this funnel needs to observe. If a send still fails (offline,
   5xx, etc.) events are queued in localStorage and retried on a timer + the
   next track() call. The queue is capped so a permanently-dead endpoint can
   never grow storage without bound.

   Privacy rule (TASK-196, issue #315): an event's `path` is the normalized
   screen path from lib/screenPath.js (the same key ScreenTraceBridge uses),
   never location.search — the SPA's URLs carry reset/verify tokens, Stripe
   session ids and OAuth return params.

   Event catalogue (event — firing surface):
     signup_completed — AuthContext after password signup
     onboarding_started / onboarding_step / onboarding_skipped — OnboardingChat
     onboarding_completed / social_connected / first_generation /
       first_post_created — OnboardingActivation
     first_post_created — CreatePost after a successful batch create
     first_publish — scheduler API and OnboardingActivation publish success
     card_added / checkout_completed — App billing gate; card_added also fires
       on the Settings hosted-checkout return (trackOnce deduplicates it)
     chatgpt_returned — reserved for a truthful completed return (the SPA's
       navigation to MCP permission consent deliberately does not emit it)
     paywall_shown / paywall_dismissed — UpgradeContext
     preview_paywall_shown / preview_paywall_dismissed /
       preview_payment_fix_clicked / preview_subscribe_clicked — PreviewContext
     subscribe_clicked — UpgradeModal
     nudge_shown / nudge_clicked / nudge_dismissed — OnboardingNudge
     tour_started / tour_step / tour_completed / tour_skipped — ProductTour
     screen_view / first_action / screen_exit_no_action — ScreenTraceBridge
     setup_card_shown / setup_card_clicked — dashboard FinishSetupCard
     quick_brand_created — QuickBrandSheet after canonical profile creation
     activation_card_shown / activation_card_open_plan — dashboard
       ActivationProgressCard (TASK-296)
     plan_intent_applied — PlanSelectScreen, once, when a persisted ?plan=
       intent (lib/planIntent.js) actually wins the preselection (TASK-313)

   Hard rule: this module must NEVER throw and NEVER block the caller.
   track()/trackOnce() are fire-and-forget — every public function wraps
   its body in try/catch, mirroring utils/uxTracker.js's fail-silent design.
   ============================================================ */
import { funnelAnalyticsEnabled } from './flags.js';
import { normalizeScreen } from './screenPath.js';
import { gtagEvent, mapFunnelEventToGtag } from './gtag.js';

const QUEUE_KEY = 'dathent.funnel.queue';
const MAX_QUEUE = 200;
const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 15000;
const ENDPOINT_PATH = '/api/funnel/ingest';

let apiBase = '';
let userId = null;
let flushTimer = null;
let flushing = false;
let started = false;

function isDev() {
  try {
    // Vite exposes import.meta.env.DEV; guarded for non-Vite/test contexts.
    return Boolean(import.meta && import.meta.env && import.meta.env.DEV);
  } catch (_) {
    return false;
  }
}

function readQueue() {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function writeQueue(queue) {
  try {
    // Cap from the front — keep the MOST RECENT events if the endpoint has
    // been dead a while, rather than growing storage or endlessly retrying
    // the oldest stale batch.
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch (_) { /* storage unavailable/full — best effort only, event is lost */ }
}

function enqueue(event) {
  const queue = readQueue();
  queue.push(event);
  writeQueue(queue);
}

// Sends the oldest MAX_BATCH queued events. On success, removes exactly that
// batch from the queue (a differently-sized queue read mid-flight — e.g. a
// track() call from another tab — is fine; we only ever drop what we know
// we sent). On any failure (offline, 4xx/5xx) the whole queue is left
// untouched for the next retry.
async function flush() {
  if (flushing) return;
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  const queue = readQueue();
  if (!queue.length) return;
  flushing = true;
  try {
    const batch = queue.slice(0, MAX_BATCH);
    const res = await fetch(`${apiBase}${ENDPOINT_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
    if (res && res.ok) {
      writeQueue(readQueue().slice(batch.length));
    }
  } catch (_) {
    // network error / offline / endpoint missing — keep queued, retry later.
  } finally {
    flushing = false;
  }
}

// Wire the tracker to the gateway base URL and start the background retry
// timer. Safe to call multiple times (idempotent) and safe to call before
// the flag is known to be on — it only starts the timer when enabled.
export function initFunnelTracker({ apiBase: base = '' } = {}) {
  try {
    apiBase = base || '';
    if (!funnelAnalyticsEnabled()) return;
    if (started || typeof window === 'undefined') return;
    started = true;
    flushTimer = window.setInterval(() => { flush().catch(() => {}); }, FLUSH_INTERVAL_MS);
    window.addEventListener('pagehide', () => { flush().catch(() => {}); });
    flush().catch(() => {});
  } catch (_) { /* tracker must never block boot */ }
}

// Tie queued/future events to the signed-in account. Mirrors
// uxTracker.identifyUxUser — call with null on logout.
export function identifyFunnelUser(id) {
  try {
    userId = id || null;
  } catch (_) { /* noop */ }
}

// track(eventName, props) — the funnel event API. Fire-and-forget: callers
// never need to await or catch this. No-ops entirely while the flag is off.
export function track(eventName, props) {
  // Google Ads mirror is independent of the internal funnel flag.
  try {
    const gName = mapFunnelEventToGtag(String(eventName || ''));
    if (gName) gtagEvent(gName, props && typeof props === 'object' ? props : undefined);
  } catch (_) { /* never throw */ }
  try {
    if (!funnelAnalyticsEnabled()) return;
    if (typeof window === 'undefined') return;
    const event = {
      name: String(eventName || '').slice(0, 64),
      props: (props && typeof props === 'object') ? props : undefined,
      ts: Date.now(),
      userId,
      // Normalized screen path ONLY (TASK-196, issue #315) — never the query
      // string: ?token=, ?session_id=, OAuth return params must not be sent.
      path: (typeof location !== 'undefined') ? normalizeScreen(location.pathname) : null,
    };
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.debug('[funnel]', event.name, event.props || {});
    }
    enqueue(event);
    flush().catch(() => {});
  } catch (_) { /* tracker must never throw */ }
}

// Fire an event at most once per (eventName, scope) for this browser —
// used for "first ever" semantics (first_generation, etc.) where a true
// server-side dedup doesn't exist yet. Best-effort only: a different
// browser, incognito window, or cleared storage can refire it — that's a
// known limitation of doing this client-side (see TASK-122 PR notes).
export function trackOnce(eventName, props, scopeKey) {
  try {
    if (!funnelAnalyticsEnabled()) return;
    const key = `dathent.funnel.once.${eventName}.${scopeKey ?? userId ?? 'anon'}`;
    if (window.localStorage.getItem(key) === '1') return;
    window.localStorage.setItem(key, '1');
  } catch (_) { /* storage unavailable — fall through and track anyway below */ }
  track(eventName, props);
}
